import React, { useEffect, useState } from "react";
import { EventBus } from "@engine/EventBus.js";
import { PartyStore } from "@store/PartyStore.js";
import { resolveLongRest } from "@engine/party/rest.js";
import { maxSpellSlotsAtLevel } from "@engine/party/levelUp.js";
import { dataRegistry } from "@engine/loaders/DataRegistry.js";
import { pushFeed } from "@engine/Feed.js";

/**
 * C08 wiring — long-rest confirm prompt.
 *
 * main.tsx emits `ui:camp_prompt` when the party steps onto a `camp` cell
 * and no hostiles are within the eligibility radius. Confirming resolves
 * a long rest (full HP, death saves cleared, spell slots restored to each
 * caster's class table at their current level) and emits
 * `world:long_rest_completed` so persistence autosaves.
 */
export function CampPrompt() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    return EventBus.on("ui:camp_prompt", () => setOpen(true));
  }, []);

  if (!open) return null;

  const close = () => {
    setOpen(false);
    setBusy(false);
  };

  const rest = () => {
    if (busy) return;
    setBusy(true);
    void (async () => {
      const members = PartyStore.getMembers();
      const caps = new Map<string, Record<number, number>>();
      for (const m of members) {
        const record = await dataRegistry.getClass(m.characterClass.toLowerCase());
        if (record) caps.set(m.instanceId, maxSpellSlotsAtLevel(record, m.level));
      }
      const result = resolveLongRest(members, { maxSpellSlots: caps });
      if (result.success) {
        PartyStore.setMembers([...result.nextMembers]);
        EventBus.emit("ui:party_update", {});
        EventBus.emit("world:long_rest_completed", {
          memberIds: members.map((m) => m.instanceId),
        });
        pushFeed("The party makes camp. Wounds close and spirits mend.");
        for (const id of result.revivedIds) {
          const name = members.find((m) => m.instanceId === id)?.name ?? "An ally";
          pushFeed(`${name} stirs — alive again.`);
        }
      } else {
        pushFeed("The party cannot rest here.");
      }
      close();
    })();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "auto",
        zIndex: 200,
      }}
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#1a1a1a",
          border: "1px solid rgba(255,255,255,0.2)",
          borderRadius: 8,
          padding: 24,
          minWidth: 280,
          maxWidth: 400,
          fontFamily: "monospace",
        }}
      >
        <h3 style={{ margin: "0 0 12px", color: "white", fontSize: 14, letterSpacing: 2 }}>
          MAKE CAMP
        </h3>
        <p style={{ color: "#ccc", fontSize: 12, margin: "0 0 16px", lineHeight: 1.5 }}>
          The embers of an old fire ring invite a long rest. Sleep here to restore the
          party&apos;s health and spells?
        </p>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" onClick={rest} disabled={busy} style={primaryBtn}>
            {busy ? "Resting…" : "Long Rest"}
          </button>
          <button type="button" onClick={close} style={secondaryBtn}>
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

const primaryBtn: React.CSSProperties = {
  padding: "6px 16px",
  fontFamily: "monospace",
  fontSize: 12,
  background: "#2a4060",
  color: "#c8e6ff",
  border: "1px solid rgba(100,160,220,0.4)",
  borderRadius: 4,
  cursor: "pointer",
};

const secondaryBtn: React.CSSProperties = {
  padding: "6px 16px",
  fontFamily: "monospace",
  fontSize: 12,
  background: "rgba(0,0,0,0.4)",
  color: "#aaa",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: 4,
  cursor: "pointer",
};
